import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import Aggrid from "../../framework/AgGrid";
import Button from "../../framework/Button";
import CellNumberEditor from "../../framework/cellEditor/CellNumberEditor";
import CellSelectInput from "../../framework/cellEditor/CellSelectInput";
import CellTextEditor from "../../framework/cellEditor/CellTextEditor";
import CellTreeSelect from "../../framework/cellEditor/CellTreeSelect";
import GlobalContext from "../../framework/GlobalContext";
import Shortcut from "../../framework/Shortcut";
import { displayToast } from "../../framework/toast/toastUtils";
import { searchManufacturerGroup } from "../../services/manufacturer-group-services";
import { searchManufacturer } from "../../services/manufacturer-services";
import {
  cellTabbed,
  debounceFocusCell,
  focusCell,
  rowDown,
  rowUp,
  selectRow,
  stopEditing,
} from "../../utils/gridUtils";
import { getStringValue } from "../../utils/utils";
import { CategoryList, newDiscountProgram } from "./DiscountProgramConstants";
import DiscountProgramContext from "./DiscountProgramContext";
import classes from "./DiscountProgramGrid.module.css";
import ManufacturerCellSelectEditor from "./ManufacturerCellSelectEditor";
import ManufacturerGroupCellSelectEditor from "./ManufacturerGroupCellSelectEditor";

const DiscountProgramGrid = () => {
  const [api, setApi] = useState(null);
  const [columnApi, setColumnApi] = useState(null);
  const [manufacturerOptions, setManufacturerOptions] = useState([]);
  const manufacturerOptionsRef = useRef([]);
  const searchManufacturerRef = useRef(null);
  const [manufacturerGroupOptions, setManufacturerGroupOptions] = useState([]);
  const manufacturerGroupOptionsRef = useRef([]);
  const searchManufacturerGroupRef = useRef(null);
  const { discountProgramGridRef, addClicked, setAddClicked, addClickedRef } =
    useContext(DiscountProgramContext);

  const {
    setCurrentComponent,
    currentComponent,
    findCategory,
    createTreeSelectOptions,
  } = useContext(GlobalContext);

  const { discountProgramRowData, gridColumns } = useSelector(
    (state) => state.commonReducer.tabs[state.commonReducer.activeTabIndex]
  );

  const { categoryOptions } = useSelector((state) => state.commonReducer);
  const dispatch = useDispatch();

  const serachManufacturerHandler = useCallback(async (searchText) => {
    const term = (searchText || "").trim();
    const response = await searchManufacturer(`manufacturerName=${term}`);
    const rows = Array.isArray(response?.result?.rows)
      ? response.result.rows
      : Array.isArray(response?.rows)
      ? response.rows
      : [];
    const options = rows.map((m) => ({
      id: m.manufacturerId,
      label: m.manufacturerName,
    }));
    setManufacturerOptions(options);
    return options;
  }, []);

  const searchManufacturerGroupHandler = useCallback(async (searchText) => {
    const term = (searchText || "").trim();
    const rows = await searchManufacturerGroup({
      page: 1,
      limit: 20,
      name: term,
    });
    const options = (rows || []).map((g) => ({
      id: g.manufacturerGroupId,
      label: g.name,
    }));
    setManufacturerGroupOptions(options);
    return options;
  }, []);

  useEffect(() => {
    manufacturerOptionsRef.current = manufacturerOptions;
  }, [manufacturerOptions]);

  useEffect(() => {
    searchManufacturerRef.current = serachManufacturerHandler;
  }, [serachManufacturerHandler]);

  useEffect(() => {
    manufacturerGroupOptionsRef.current = manufacturerGroupOptions;
  }, [manufacturerGroupOptions]);

  useEffect(() => {
    searchManufacturerGroupRef.current = searchManufacturerGroupHandler;
  }, [searchManufacturerGroupHandler]);

  const onGridReady = (params) => {
    setApi(params.api);
    setColumnApi(params.columnApi);
    params.api.sizeColumnsToFit();
  };

  const getOptionGroupId = useCallback((row) => {
    if (!row || !row.option) return undefined;
    const categoryId = row.option?.categoryId || row.option?.value?.categoryId;
    if (categoryId) return `category:${categoryId}`;
    if (row.category?.id === "manufacturer") {
      const manufacturerId = row.option?.id || row.option?.manufacturerId;
      return manufacturerId ? `manufacturer:${manufacturerId}` : undefined;
    }
    if (row.category?.id === "manufacturer_group") {
      const groupId = row.option?.id || row.option?.manufacturerGroupId;
      return groupId ? `manufacturer_group:${groupId}` : undefined;
    }
    return undefined;
  }, []);

  const addProgram = useCallback(() => {
    let updatedDiscountProgram = { ...newDiscountProgram };
    let rowData = [...discountProgramRowData];
    if (rowData.length > 0) {
      updatedDiscountProgram = {
        ...updatedDiscountProgram,
        option: rowData[rowData.length - 1].option || "",
      };

      // Auto-set minimum value when adding a new slab with the same option
      if (updatedDiscountProgram.option) {
        const optionId = getOptionGroupId({
          option: updatedDiscountProgram.option,
          category: rowData[rowData.length - 1]?.category,
        });

        // Collect rows with the same option
        const matchingRows = rowData
          .map((row, index) => ({ ...row, originalIndex: index }))
          .filter((row) => row.option && getOptionGroupId(row) === optionId)
          .sort((a, b) => a.min - b.min);

        // If there are previous slabs with same option, set min to previous max + 1
        if (matchingRows.length > 0) {
          const lastSlab = matchingRows[matchingRows.length - 1];
          if (lastSlab.max !== undefined) {
            updatedDiscountProgram.min = lastSlab.max + 1;
          }
        }
      }
    }
    rowData = [...discountProgramRowData, updatedDiscountProgram];
    dispatch({ type: "update_discount_program_grid", payload: rowData });
    setTimeout(() => {
      selectRow(api, rowData.length - 1);
      debounceFocusCell(
        api,
        rowData.length - 1,
        columnApi?.getColumn("category")
      );
    }, 100);
    setAddClicked(false);
  }, [
    api,
    columnApi,
    dispatch,
    setAddClicked,
    discountProgramRowData,
    getOptionGroupId,
  ]);

  const removeProduct = useCallback(() => {
    if (api?.getSelectedNodes()?.length === 0) {
      return displayToast({
        type: "warn",
        title: "Please select a discount program to delete.",
      });
    }
    let rowData = [...discountProgramRowData];
    let selectedIndex = api.getSelectedNodes()[0].rowIndex;
    rowData.splice(selectedIndex, 1);
    if (rowData.length === 0) {
      rowData.push(newDiscountProgram);
    }
    dispatch({
      type: "update_discount_program_grid",
      payload: [...rowData],
    });
    if (rowData.length === 0) return;
    selectRow(
      api,
      selectedIndex === rowData.length ? selectedIndex - 1 : selectedIndex
    );
    debounceFocusCell(
      api,
      selectedIndex === rowData.length ? selectedIndex - 1 : selectedIndex,
      columnApi?.getColumn("category")
    );
  }, [api, columnApi, dispatch, discountProgramRowData]);

  const mainGridMarkers = useMemo(() => {
    return {
      altDown: "discount_program_name",
      altUp: "discount_program_name",
      left: "grid_discount_program",
      right: "grid_discount_program",
      down: "discount_program_name",
      up: "discount_program_name",
    };
  }, []);
  const gridEditingStopped = useCallback(
    (direction) => {
      if (api) stopEditing(api);
      if (currentComponent === "grid_discount_program") {
        const rowIndex =
          direction === "right" ? 0 : api.getDisplayedRowCount() - 1;
        selectRow(api, rowIndex);
        return discountProgramGridRef.current.focus(
          direction !== "right",
          "category"
        );
      }

      if (direction && mainGridMarkers[direction]) {
        document
          .getElementById(mainGridMarkers[direction])
          .focus({ preventScroll: true });
        setCurrentComponent(mainGridMarkers[direction]);
      }
    },
    [
      api,
      currentComponent,
      mainGridMarkers,
      discountProgramGridRef,
      setCurrentComponent,
    ]
  );
  const defaultColDef = {
    flex: 1,
    cellStyle: (params) => {
      return {
        fontSize: 14,
        fontWeight: 500,
      };
    },
    cellEditor: "cellTextEditor",
    cellEditorParams: (params) => {
      return {
        value: params?.value || "",
      };
    },
    // onCellValueChanged: (params) => cellValueChanged(params.node, params),
    cellRendererFramework: (params) => {
      return params.colDef.editable ? (
        <div className="ag-editable-cell">
          <div>{getStringValue(params.value)}</div>
        </div>
      ) : (
        getStringValue(params.value)
      );
    },
    suppressKeyboardEvent: (params) => true,
    editable: true,
  };
  const tabHandler = useCallback(
    (shiftKey) => {
      if (!cellTabbed(api, shiftKey)) {
        gridEditingStopped(shiftKey ? "up" : "right");
      }
    },
    [api, gridEditingStopped]
  );

  const cellValueChanged = useCallback(
    (node, changedField) => {
      let updatedRowData = {};
      let updatedReduxData = [];
      updatedRowData = node.data;
      updatedReduxData = [...discountProgramRowData];
      updatedReduxData[node.rowIndex] = updatedRowData;

      if (
        node.data.option &&
        (node.data.min !== undefined || node.data.max !== undefined)
      ) {
        const optionId = getOptionGroupId(node.data);
        const currentRowIndex = node.rowIndex;

        // Collect rows with the same option
        const matchingRows = updatedReduxData
          .map((row, index) => ({ ...row, originalIndex: index }))
          .filter((row) => row.option && getOptionGroupId(row) === optionId)
          .sort((a, b) => a.min - b.min);

        const currentSortedRow = matchingRows.find(
          (row) => row.originalIndex === currentRowIndex
        );

        if (!currentSortedRow) return;

        const currentSortedIndex = matchingRows.indexOf(currentSortedRow);
        const currentMin = node.data.min;
        // Only show validation toasts when max field is changed
        if (changedField === "min") {
          // Validation 1: If there are previous slabs with same option, min should start from previous slab's max
          if (currentSortedIndex > 0) {
            const prevRow = matchingRows[currentSortedIndex - 1];
            const expectedMin = prevRow.max + 1;

            if (currentMin < expectedMin) {
              displayToast({
                type: "error",
                title: `Minimum should be (previous slab's max + 1)`,
              });
              updatedRowData.min = expectedMin;
              updatedReduxData[currentRowIndex] = updatedRowData;
              dispatch({
                type: "update_discount_program_grid",
                payload: [...updatedReduxData],
              });
              return;
            }
          }
        }

        // Removed from onChnage as asked by testing team and keep it on save
        // if (changedField === "max") {
        //   // Validation 2: Max should be greater than min
        //   if (
        //     currentMax !== undefined &&
        //     currentMin !== undefined &&
        //     currentMax <= currentMin
        //   ) {
        //     displayToast({
        //       type: "error",
        //       title: "Maximum value must be greater than minimum value",
        //     });
        //     return;
        //   }
        // }
      }

      dispatch({
        type: "update_discount_program_grid",
        payload: [...updatedReduxData],
      });
      updatedRowData = {};
      updatedReduxData = [];
    },
    [dispatch, discountProgramRowData, getOptionGroupId]
  );

  const gridListener = useCallback(
    (e) => {
      if (!api) return;
      const key = e.key;
      const shiftKey = e.shiftKey;
      const altKey = e.altKey;
      const ctrlKey =
        api.getSelectedNodes()[0]?.rowIndex === 0 ? false : e.ctrlKey;

      const isAutocompleteOpen =
        document.getElementsByClassName("MuiAutocomplete-listbox").length > 0;
      const isTreeSelectOpen =
        document.getElementsByClassName("tree-select-popper").length > 0;
      const isDropdownOpen = isAutocompleteOpen || isTreeSelectOpen;

      if (key === "Tab") {
        tabHandler(shiftKey);
        return;
      }
      if (key === "Enter") {
        tabHandler(shiftKey);
        return;
      }
      if (key === "ArrowDown") {
        if (altKey) {
          gridEditingStopped("altDown");
          return;
        }

        if (isDropdownOpen) {
          return;
        }

        const lastRowIndex = api.getDisplayedRowCount() - 1;
        const currentRowIndex = api.getFocusedCell()?.rowIndex;

        if (currentRowIndex === lastRowIndex) {
          document
            .getElementById("discount_program_name")
            .focus({ preventScroll: true });
          setCurrentComponent("discount_program_name");
          return;
        }

        if (api.getFocusedCell().column.colDef.editable) {
          if (!rowDown(api, ctrlKey, api.getFocusedCell().column))
            gridEditingStopped("down");
          return;
        }
        if (!rowDown(api, ctrlKey, columnApi.getColumn("category")))
          gridEditingStopped("down");
        return;
      }
      if (key === "ArrowUp") {
        if (altKey) {
          gridEditingStopped("altUp");
          return;
        }
        if (isDropdownOpen) {
          return;
        }
        if (api.getFocusedCell().column.colDef.editable) {
          if (!rowUp(api, ctrlKey, api.getFocusedCell().column))
            gridEditingStopped("up");
          return;
        }
        if (!rowUp(api, ctrlKey, columnApi.getColumn("category")))
          gridEditingStopped("up");
        return;
      }
      if (key === "Delete") {
        removeProduct();
        return;
      }
    },
    [
      api,
      columnApi,
      gridEditingStopped,
      removeProduct,
      tabHandler,
      setCurrentComponent,
    ]
  );

  useEffect(() => {
    if (addClicked) {
      addProgram();
    }
  }, [addClicked, addProgram]);

  useEffect(() => {
    if (api && gridColumns && gridColumns.length > 0) {
      const newGridColumns = gridColumns.map((column) => {
        if (column.field === "category") {
          return {
            ...column,
            cellEditorParams: (params) => {
              return {
                value: params.data?.category || CategoryList[0] || null,
                options: CategoryList,
                allowMultiple: false,
                showPath: false,
                getOptionLabel: (option) => option?.label || "",
                isOptionEqualToValue: (option, value) =>
                  option?.categoryId === value?.categoryId,
                onChange: (value) => {
                  if (value) {
                    const _data = [...discountProgramRowData];
                    _data[params.node.rowIndex]["category"] = value;
                    dispatch({
                      type: "update_discount_program_grid",
                      payload: _data,
                    });
                    params.api.redrawRows();
                    setTimeout(() => {
                      params.api.setFocusedCell(
                        params.node.rowIndex,
                        params.column
                      );
                    }, 1);
                  }
                },
              };
            },
            valueGetter: (params) => {
              return (
                params.data?.category?.label || CategoryList[0]?.label || ""
              );
            },
            valueFormatter: (params) => {
              return params.value || "";
            },
          };
        }
        if (column.field === "option") {
          return {
            ...column,
            editable: true,
            cellEditorSelector: (params) => {
              if (params.data?.category?.id === "manufacturer") {
                return { component: "manufacturerCellSelectEditor" };
              }
              if (params.data?.category?.id === "manufacturer_group") {
                return { component: "manufacturerGroupCellSelectEditor" };
              }
              return { component: "cellTreeSelect" };
            },
            valueGetter: (params) => {
              if (params.data?.category?.id === "manufacturer") {
                return params.data?.option?.label || "";
              }
              if (params.data?.category?.id === "manufacturer_group") {
                return params.data?.option?.label || "";
              }
              return (
                params.data?.option?.categoryName ||
                params.data?.option?.value?.categoryName ||
                ""
              );
            },
            valueFormatter: (params) => {
              return params.value || "";
            },
            cellRendererFramework: (params) => {
              let value = "";
              if (params.data?.category?.id === "manufacturer") {
                value = params.data?.option?.label || "";
              } else if (params.data?.category?.id === "manufacturer_group") {
                value = params.data?.option?.label || "";
              } else {
                value =
                  params.data?.option?.categoryName ||
                  params.data?.option?.value?.categoryName ||
                  "";
              }

              if (!value) {
                return (
                  <div className="ag-editable-cell">
                    <div style={{ color: "#999" }}>Type here...</div>
                  </div>
                );
              }

              return (
                <div className="ag-editable-cell">
                  <div style={{ width: "100%" }}>{value}</div>
                </div>
              );
            },
            cellEditorParams: (params) => {
              if (params.data?.category?.id === "manufacturer") {
                return {
                  value: params.data?.option || null,
                  options: manufacturerOptionsRef.current,
                  allowMultiple: false,
                  showPath: false,
                  freeSolo: true,
                  getOptionLabel: (option) => option?.label || option,
                  isOptionEqualToValue: (option, value) => {
                    const optionId = option?.id ?? option?.manufacturerId;
                    const valueId = value?.id ?? value?.manufacturerId;
                    if (optionId && valueId) return optionId === valueId;
                    return option?.label === (value?.label || value);
                  },
                  onInputChange: (e, val) => {
                    const text =
                      typeof val === "string" ? val : e?.target?.value || "";
                    Promise.resolve().then(() => {
                      searchManufacturerRef.current &&
                        searchManufacturerRef.current(text);
                    });
                  },
                  onChange: (value) => {
                    const _data = [...discountProgramRowData];
                    const updatedRow = {
                      ...params.data,
                      option: value,
                    };
                    const currentRowIndex = params.node.rowIndex;
                    const newOptionId = getOptionGroupId({ ...updatedRow });
                    const matchingRows = _data
                      .map((row, index) => ({ ...row, originalIndex: index }))
                      .filter((row, index) => {
                        if (index === currentRowIndex) return false;
                        return (
                          row.option && getOptionGroupId(row) === newOptionId
                        );
                      })
                      .sort((a, b) => a.min - b.min);

                    if (matchingRows.length > 0) {
                      const lastSlab = matchingRows[matchingRows.length - 1];
                      if (lastSlab.max !== undefined) {
                        updatedRow.min = lastSlab.max + 1;
                      }
                    } else {
                      updatedRow.min = 0;
                    }
                    _data[currentRowIndex] = updatedRow;
                    dispatch({
                      type: "update_discount_program_grid",
                      payload: _data,
                    });
                    if (updatedRow.min === 0) {
                      focusCell(
                        discountProgramGridRef.current.api,
                        params.node.rowIndex,
                        discountProgramGridRef.current.columnApi.getColumn(
                          "max"
                        )
                      );
                      return;
                    }
                    focusCell(
                      discountProgramGridRef.current.api,
                      params.node.rowIndex,
                      discountProgramGridRef.current.columnApi.getColumn("min")
                    );
                  },
                };
              } else if (params.data?.category?.id === "manufacturer_group") {
                return {
                  value: params.data?.option || null,
                  options: manufacturerGroupOptionsRef.current,
                  allowMultiple: false,
                  showPath: false,
                  freeSolo: true,
                  getOptionLabel: (option) => option?.label || option,
                  isOptionEqualToValue: (option, value) => {
                    const optionId = option?.id ?? option?.manufacturerGroupId;
                    const valueId = value?.id ?? value?.manufacturerGroupId;
                    if (optionId && valueId) return optionId === valueId;
                    return option?.label === (value?.label || value);
                  },
                  onInputChange: (e, val) => {
                    const text =
                      typeof val === "string" ? val : e?.target?.value || "";
                    Promise.resolve().then(() => {
                      searchManufacturerGroupRef.current &&
                        searchManufacturerGroupRef.current(text);
                    });
                  },
                  onChange: (value) => {
                    const _data = [...discountProgramRowData];
                    const updatedRow = {
                      ...params.data,
                      option: value,
                    };
                    const currentRowIndex = params.node.rowIndex;
                    const newOptionId = getOptionGroupId({ ...updatedRow });
                    const matchingRows = _data
                      .map((row, index) => ({ ...row, originalIndex: index }))
                      .filter((row, index) => {
                        if (index === currentRowIndex) return false;
                        return (
                          row.option && getOptionGroupId(row) === newOptionId
                        );
                      })
                      .sort((a, b) => a.min - b.min);
                    if (matchingRows.length > 0) {
                      const lastSlab = matchingRows[matchingRows.length - 1];
                      if (lastSlab.max !== undefined) {
                        updatedRow.min = lastSlab.max + 1;
                      }
                    } else {
                      updatedRow.min = 0;
                    }
                    _data[currentRowIndex] = updatedRow;
                    dispatch({
                      type: "update_discount_program_grid",
                      payload: _data,
                    });
                    if (updatedRow.min === 0) {
                      focusCell(
                        discountProgramGridRef.current.api,
                        params.node.rowIndex,
                        discountProgramGridRef.current.columnApi.getColumn(
                          "max"
                        )
                      );
                      return;
                    }
                    focusCell(
                      discountProgramGridRef.current.api,
                      params.node.rowIndex,
                      discountProgramGridRef.current.columnApi.getColumn("min")
                    );
                  },
                };
              } else {
                let _value = findCategory(
                  categoryOptions,
                  params.data?.option?.categoryId ||
                    params.data?.option?.value?.categoryId
                );
                _value = _value ? createTreeSelectOptions(_value) : null;
                return {
                  value: _value,
                  options: categoryOptions,
                  allowMultiple: false,
                  showPath: false,
                  inputProps: {
                    placeholder: "Type here...",
                  },
                  onChange: (e, value) => {
                    const _data = [...discountProgramRowData];
                    const updatedRow = {
                      ...params.data,
                      option: value,
                    };
                    const currentRowIndex = params.node.rowIndex;
                    const newOptionId =
                      value?.categoryId || value?.value?.categoryId;
                    const matchingRows = _data
                      .map((row, index) => ({ ...row, originalIndex: index }))
                      .filter((row, index) => {
                        if (index === currentRowIndex) return false;
                        const rowOptionId =
                          row.option?.categoryId ||
                          row.option?.value?.categoryId;
                        return row.option && rowOptionId === newOptionId;
                      })
                      .sort((a, b) => a.min - b.min);
                    if (matchingRows.length > 0) {
                      const lastSlab = matchingRows[matchingRows.length - 1];
                      if (lastSlab.max !== undefined) {
                        updatedRow.min = lastSlab.max + 1;
                      }
                    } else {
                      updatedRow.min = 0;
                    }
                    _data[currentRowIndex] = updatedRow;
                    dispatch({
                      type: "update_discount_program_grid",
                      payload: _data,
                    });
                    if (updatedRow.min === 0) {
                      focusCell(
                        discountProgramGridRef.current.api,
                        params.node.rowIndex,
                        discountProgramGridRef.current.columnApi.getColumn(
                          "max"
                        )
                      );
                      return;
                    }
                    focusCell(
                      discountProgramGridRef.current.api,
                      params.node.rowIndex,
                      discountProgramGridRef.current.columnApi.getColumn("min")
                    );
                  },
                };
              }
            },
          };
        }
        if (column.field === "min") {
          return {
            ...column,
            cellEditor: "cellNumberEditor",
            editable: (params) => {
              const rowData = params.api.getDisplayedRowAtIndex(
                params.node.rowIndex
              ).data;
              const optionId = getOptionGroupId(rowData);
              const allRows = params.api.getDisplayedRowCount();
              let isFirstSlab = true;
              for (let i = 0; i < allRows; i++) {
                if (i === params.node.rowIndex) continue;
                const data = params.api.getDisplayedRowAtIndex(i).data;
                const otherOptionId = getOptionGroupId(data);
                if (optionId && otherOptionId === optionId && data.min === 0) {
                  isFirstSlab = false;
                  break;
                }
              }
              return !(rowData.min === 0 && isFirstSlab);
            },
            cellEditorParams: (params) => {
              return {
                value: params?.value || 0,
                onChange: (values) => {
                  params.node.data[column.field] = values.floatValue;
                  cellValueChanged(params.node, column.field);
                },
              };
            },
          };
        }
        if (column.field === "max" || column.field === "discPerDefault") {
          return {
            ...column,
            cellEditor: "cellNumberEditor",
            cellEditorParams: (params) => {
              return {
                value: params?.value || 0,
                onChange: (values) => {
                  params.node.data[column.field] = values.floatValue;
                  cellValueChanged(params.node, column.field);
                },
              };
            },
          };
        }
        return column;
      });
      api.setColumnDefs([...newGridColumns]);
    }
  }, [
    api,
    categoryOptions,
    cellValueChanged,
    createTreeSelectOptions,
    discountProgramGridRef,
    discountProgramRowData,
    dispatch,
    findCategory,
    gridColumns,
    getOptionGroupId,
  ]);

  useEffect(() => {
    if (currentComponent !== "grid_discount_program") {
      gridEditingStopped();
    }
  }, [currentComponent, gridEditingStopped]);

  useEffect(() => {
    if (currentComponent === "grid_discount_program") {
      document.addEventListener("keyup", gridListener, false);
    }
    return () => {
      document.removeEventListener("keyup", gridListener, false);
    };
  }, [currentComponent, gridListener]);
  return (
    <>
      <div
        id="purchase_product_container"
        className={`${classes["product-grid-parent"]} ${classes["discountProgram-bill-hide"]}`}
      >
        <div
          className={`${classes["product-grid-discountProgram-bill-container"]}`}
        >
          <div className={`${classes["product-grid-container"]} `}>
            <div className={classes["product-grid"]}>
              <Aggrid
                id="grid_discount_program"
                ref={discountProgramGridRef}
                rowData={discountProgramRowData}
                defaultColDef={defaultColDef}
                onGridReady={onGridReady}
                frameworkComponents={{
                  cellTextEditor: CellTextEditor,
                  cellSelectInput: CellSelectInput,
                  cellNumberEditor: CellNumberEditor,
                  cellTreeSelect: CellTreeSelect,
                  manufacturerCellSelectEditor: ManufacturerCellSelectEditor,
                  manufacturerGroupCellSelectEditor:
                    ManufacturerGroupCellSelectEditor,
                }}
                onCellFocused={(params) => {
                  setCurrentComponent("grid_discount_program");
                }}
              />
            </div>
            <div className={classes["product-grid-controls"]}>
              <div
                className={classes["product-grid-control-columns-start"]}
              ></div>
              <div className={classes["product-grid-control-columns-center"]}>
                <div
                  className={
                    classes["product-grid-control-columns-center-button"]
                  }
                >
                  <Button
                    label="ADD"
                    ref={addClickedRef}
                    startIcon={<Shortcut keys={["+"]} />}
                    onClick={addProgram}
                  />
                </div>
                <div
                  className={
                    classes["product-grid-control-columns-center-button"]
                  }
                >
                  <Button
                    label="Delete"
                    startIcon={<Shortcut keys={["Del"]} />}
                    onClick={removeProduct}
                  />
                </div>
              </div>
              <div
                className={classes["product-grid-control-columns-end"]}
              ></div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DiscountProgramGrid;
